import { useMemo } from "react";
import { Controller, FieldError, FormProvider, useForm } from "react-hook-form";
import { useSelector } from "react-redux";
import { yupResolver } from "@hookform/resolvers/yup";
import { mdiLoading } from "@mdi/js";
import Icon from "@mdi/react";
import dayjs from "dayjs";
import * as Yup from "yup";

import Button from "components/Button";
import Input from "components/Input";
import Select from "components/Select";
import { FR_CONTRACT_TYPE } from "constants/haulpay-broker";
import { EQUIPMENT_TYPES } from "constants/signup";
import DestinationInputGroup from "features/main/Loads/modules/DestinationInputGroup";
import OriginInputGroup from "features/main/Loads/modules/OriginInputGroup";
import { IFundingRequestForm } from "models/HaulPay";
import { AddressInfo } from "models/User";
import { selectUserPermission } from "store/auth/selector";
import styles from "./style.module.scss";

const LoadInfoFormSchema = Yup.object<Partial<IFundingRequestForm>>({
  contractType: Yup.object().required("This field is required."),
  originLocations: Yup.array().of(
    Yup.object().shape({
      origin: Yup.object<AddressInfo>()
        .shape({
          city: Yup.string().required("Must include city and state"),
          state: Yup.string().required("Must include city and state")
        })
        .required("This Field is required")
    })
  ),
  destinationLocations: Yup.array().of(
    Yup.object().shape({
      destination: Yup.object<AddressInfo>()
        .shape({
          city: Yup.string().required("Must include city and state"),
          state: Yup.string().required("Must include city and state")
        })
        .required("This Field is required")
    })
  ),
  equipmentType: Yup.object().required("This field is required."),
  loadLength: Yup.number()
    .typeError("This field is required.")
    .required("This field is required.")
    .integer("Length must be an integer")
    .moreThan(1, "Length must be greater than 1 ft.")
    .max(100, "Length must be less than or equal to 100 ft."),
  loadWeight: Yup.number()
    .typeError("This field is required.")
    .required("This field is required.")
    .integer("Weight must be an integer")
    .moreThan(99, "Weight must be greater than or equal to 100 lbs.")
});

type LoadInfoProps = {
  fundingRequestFormData?: IFundingRequestForm;
  handleNext: VoidFunction;
  setFormDataCallback: (data: IFundingRequestForm) => void;
};

export default function LoadInfo({
  fundingRequestFormData,
  setFormDataCallback,
  handleNext
}: Readonly<LoadInfoProps>) {
  const userPermission = useSelector(selectUserPermission);

  const methods = useForm<Partial<IFundingRequestForm>>({
    mode: "all",
    values: fundingRequestFormData,
    resolver: yupResolver(LoadInfoFormSchema),
    defaultValues: {
      originLocations: [
        {
          origin: null,
          originName: "",
          originInstruction: ""
        }
      ],
      destinationLocations: [
        {
          destination: null,
          destinationName: "",
          destinationInstruction: ""
        }
      ],
      count: 1,
      pickUpOpenDate: dayjs().format("MM/DD/YYYY"),
      pickUpCloseDate: dayjs().add(1, "day").format("MM/DD/YYYY"),
      deliveryByDate: dayjs().add(4, "day").format("MM/DD/YYYY")
    }
  });

  const {
    register,
    control,
    handleSubmit,
    formState: { errors, isSubmitting }
  } = methods;

  const onSubmit = (data: IFundingRequestForm) => {
    setFormDataCallback(data);
    handleNext();
  };

  const filteredFRTypes = useMemo(() => {
    if (userPermission) {
      return FR_CONTRACT_TYPE.filter(
        (request) => userPermission.available_contract_types[request.value]
      );
    } else {
      return FR_CONTRACT_TYPE;
    }
  }, [userPermission]);

  return (
    <FormProvider {...methods}>
      <form className={styles.loadInfoContainer} onSubmit={handleSubmit(onSubmit)}>
        <Controller
          name="contractType"
          control={control}
          render={({ field: { value, onChange } }) => (
            <Select
              options={filteredFRTypes}
              placeholder="Contract Type"
              value={value}
              label="Funding Request Type"
              className="col-span-12"
              onChange={onChange}
              required
              error={errors.contractType as FieldError}
            />
          )}
        />

        <OriginInputGroup className="col-span-12" />

        <DestinationInputGroup className="col-span-12" />

        <Controller
          name="equipmentType"
          control={control}
          render={({ field: { value, onChange } }) => (
            <Select
              value={value}
              placeholder="Equipment Type"
              onChange={onChange}
              options={EQUIPMENT_TYPES}
              label="* Equipment Type"
              direction="up"
              className="col-span-12 desktop:col-span-4"
              error={errors.equipmentType as FieldError}
            />
          )}
        />

        <Input
          type="number"
          inputMode="numeric"
          label="Load Length"
          name="loadLength"
          className="col-span-6 desktop:col-span-4"
          register={register}
          placeholder="53"
          error={errors.loadLength}
          suffixUnit="ft"
          required
        />

        <Input
          type="number"
          inputMode="numeric"
          label="Load Weight"
          name="loadWeight"
          className="col-span-6 desktop:col-span-4"
          register={register}
          placeholder="45000"
          error={errors.loadWeight}
          suffixUnit="lbs"
          required
        />

        <div className={styles.footer}>
          <Button type="submit" size="lg" className="w-full desktop:w-44" disabled={isSubmitting}>
            {isSubmitting ? <Icon path={mdiLoading} size={2} spin={1} /> : "NEXT"}
          </Button>
        </div>
      </form>
    </FormProvider>
  );
}
